import type { ReactNode } from 'react';
import type {
  HeatLevel,
  RichmenuRankRow,
  RichmenuTabStats,
  RichmenuTapStat,
  RichmenuTapSummary,
} from '../lib/richmenuTapStats.js';
import {
  RICHMENU_TAP_FETCH_LIMIT,
  heatLevel,
  rankRows,
  summarizeTabStats,
} from '../lib/richmenuTapStats.js';
import {
  RICHMENU_LAYOUT,
  RICHMENU_LAYOUT_ITEMS,
  type RichmenuTabLayout,
} from '../lib/richmenuLayout.js';
import { RICHMENU_ITEM_LABELS } from '../lib/segment.js';
import { Card, Chip, Tile, cn, type TileFill, fillUsesWhiteText } from '@kawai/ui';
import { ChartLegend } from './analytics/ChartLegend.js';

interface RichmenuTapAnalyticsProps {
  /** `useRichmenuTapStats` の結果（読み込み中 / エラー時は undefined）。 */
  summary?: RichmenuTapSummary;
  isLoading: boolean;
  error?: Error | null;
  /**
   * タブごとの「そのタブのいずれかのボタンをタップしたユニーク人数」（M-162 /
   * `summarizeTabUniqueUsers`）。親が raw events から計算して渡す。無ければ
   * 「M 人中 K 人がタップ」チップを出さない。
   */
  tabUniqueUsers?: Record<'A' | 'B', number>;
}

/** item キー → 表示ラベル。未知キーはキーそのまま、欠落は「その他（項目不明）」。 */
function itemLabel(item: string | null): string {
  if (!item) return 'その他（項目不明）';
  return RICHMENU_ITEM_LABELS[item] ?? item;
}

/** 面の濃さ → Tile の fill（0 は fill 無し = 既定の灰面）。 */
const HEAT_FILLS: Record<Exclude<HeatLevel, 0>, TileFill> = {
  1: 'viz-stage-1',
  2: 'viz-stage-2',
  3: 'viz-stage-3',
  4: 'viz-stage-4',
};

function heatFill(level: HeatLevel): TileFill | undefined {
  return level === 0 ? undefined : HEAT_FILLS[level];
}

/** 凡例（面の濃さ 5 段）。タブ内の最多回数を 100% とした割合。 */
const HEAT_LEGEND = [
  { key: 'h0', label: '0 回', className: 'bg-surface-2' },
  { key: 'h1', label: '〜25%', className: 'bg-viz-stage-1' },
  { key: 'h2', label: '〜50%', className: 'bg-viz-stage-2' },
  { key: 'h3', label: '〜75%', className: 'bg-viz-stage-3' },
  { key: 'h4', label: '最多', className: 'bg-viz-stage-4' },
] as const;

/** 幅 %（分母 0 → 0 / 100 上限）。 */
function widthPct(value: number, max: number): number {
  if (!(max > 0) || !(value > 0)) return 0;
  return Math.min(100, (value / max) * 100);
}

/** 表示用の整数 %（分母 0 → 0）。 */
function roundPct(value: number, max: number): number {
  if (!(max > 0)) return 0;
  return Math.round((value / max) * 100);
}

/**
 * LE-00 (promo): リッチメニュータップ分析。
 *
 * 興味タグ分布（CustomerAnalytics）とは別軸で、行動ログ（promo_events）の `richmenu_tap` を
 * **実際のリッチメニュー A / B のボタン配置と同じ並び**でカード表示する（各カードに延べタップ
 * 回数 / ユニーク人数）。配置定義は `richmenuLayout.ts`（別リポ createRichMenus.ts 準拠）。
 *
 * M-162: 配置グリッドをヒートマップ化（面の濃さ = タブ内の最多に対する割合 / 各タイルに
 * 回数・人数の 2 本の棒）し、タブ見出しにチップ、カード下部に全ボタンの順位の棒を置く。
 *
 * I/O は親（CustomersListPage）が `useRichmenuTapStats` で行い、本コンポーネントは props を
 * 描画するだけ（CustomerAnalytics と同じく純表示）。
 */
export function RichmenuTapAnalytics({
  summary,
  isLoading,
  error,
  tabUniqueUsers,
}: RichmenuTapAnalyticsProps) {
  const byItem = new Map<string | null, RichmenuTapStat>(
    (summary?.rows ?? []).map((r) => [r.item, r]),
  );

  // レイアウト外 / item 不明（旧データ）の取りこぼしを「その他」に集約（完全性担保）。
  const others = (summary?.rows ?? []).filter(
    (r) => r.item === null || !RICHMENU_LAYOUT_ITEMS.has(r.item),
  );

  const tabStats = summary ? summarizeTabStats(summary, RICHMENU_LAYOUT) : [];
  const ranks = summary ? rankRows(summary, RICHMENU_LAYOUT) : [];

  return (
    <Card
      as="section"
      shadow="none"
      border="none"
      padding="none"
      aria-label="リッチメニュータップ分析"
      className="space-y-6 rounded-xl px-[30px] py-[26px]"
    >
      <div className="flex items-baseline justify-between gap-2">
        <h3 className="text-h-card font-semibold text-ink">リッチメニュータップ分析</h3>
        {summary && summary.totalTaps > 0 && (
          <span className="text-label text-ink-3">
            {summary.capped && `直近 ${RICHMENU_TAP_FETCH_LIMIT} 件 / `}延べ{' '}
            {summary.totalTaps} 回 / {summary.uniqueUsers} 人
          </span>
        )}
      </div>

      {isLoading ? (
        <p className="text-body text-ink-3">読み込み中…</p>
      ) : error ? (
        <p role="alert" className="text-body text-danger-ink">
          リッチメニュータップの集計に失敗しました: {error.message}
        </p>
      ) : !summary ? (
        <p className="text-body text-ink-2">リッチメニュータップの記録がありません。</p>
      ) : (
        <>
          {summary.totalTaps === 0 && (
            <p className="text-label text-ink-3">
              まだリッチメニュータップの記録がありません（配置のみ表示）。
            </p>
          )}

          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <span className="text-note text-ink-3">
              面の濃さ = タブ内の最多に対する割合
            </span>
            <ChartLegend items={HEAT_LEGEND} aria-label="面の濃さの凡例" />
          </div>

          {RICHMENU_LAYOUT.map((tab, i) => (
            <TabGrid
              key={tab.tab}
              tab={tab}
              byItem={byItem}
              stats={tabStats[i]}
              uniqueUsers={tabUniqueUsers?.[tab.tab]}
              totalUsers={summary.uniqueUsers}
            />
          ))}

          {others.length > 0 && (
            <div className="space-y-2.5">
              <span className="text-label font-medium text-ink-2">
                その他（配置外 / 旧データ）
              </span>
              <div className="flex flex-wrap gap-2.5">
                {others.map((r) => (
                  <StatCard key={r.item ?? '__unknown__'} item={r.item} stat={r} />
                ))}
              </div>
            </div>
          )}

          {ranks.length > 0 && <RankList rows={ranks} />}
        </>
      )}
    </Card>
  );
}

interface TabGridProps {
  tab: RichmenuTabLayout;
  byItem: Map<string | null, RichmenuTapStat>;
  /** `summarizeTabStats` の当該タブ分（summary 無しの経路では undefined）。 */
  stats?: RichmenuTabStats;
  /** そのタブのいずれかのボタンをタップしたユニーク人数（親から来なければ undefined）。 */
  uniqueUsers?: number;
  /** 全体のユニーク人数（summary.uniqueUsers = チップの M）。 */
  totalUsers: number;
}

/** 1 タブ分のカードグリッド（実ボタン配置を 6 列グリッドで再現 / M-162 ヒートマップ）。 */
function TabGrid({ tab, byItem, stats, uniqueUsers, totalUsers }: TabGridProps) {
  const cells = tab.rows.flat();
  const taps = stats?.taps ?? 0;
  const share = stats?.share ?? 0;
  const maxTaps = stats?.maxTaps ?? 0;
  const maxUsers = stats?.maxUsers ?? 0;
  return (
    <div className="space-y-2.5" data-testid={`richmenu-tab-${tab.tab}`}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-label font-medium text-ink-2">{tab.label}</span>
        <Chip tone="neutral" data-testid={`richmenu-tab-${tab.tab}-taps`}>
          延べ {taps} 回 · 全体の {share}%
        </Chip>
        {uniqueUsers !== undefined && (
          <Chip tone="neutral" data-testid={`richmenu-tab-${tab.tab}-users`}>
            {totalUsers} 人中 {uniqueUsers} 人がタップ
          </Chip>
        )}
      </div>
      <div className="grid grid-cols-6 gap-2.5">
        {cells.map((cell) => (
          <div
            key={cell.item}
            style={{ gridColumn: `span ${cell.span} / span ${cell.span}` }}
          >
            <HeatTile
              item={cell.item}
              stat={byItem.get(cell.item)}
              maxTaps={maxTaps}
              maxUsers={maxUsers}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

interface HeatTileProps {
  item: string;
  stat?: RichmenuTapStat;
  maxTaps: number;
  maxUsers: number;
}

/** 配置グリッドの 1 ボタン分（面の濃さ + 回数・人数の 2 本の棒）。 */
function HeatTile({ item, stat, maxTaps, maxUsers }: HeatTileProps) {
  const label = itemLabel(item);
  const taps = stat?.taps ?? 0;
  const users = stat?.users ?? 0;
  const level = heatLevel(taps, maxTaps);
  const fill = heatFill(level);
  const dark = fillUsesWhiteText(fill);
  return (
    <Tile
      size="sm"
      padding="sm"
      label={label}
      definition={label}
      value={taps}
      unit="回"
      fill={fill}
      data-testid={`heat-tile-${item}`}
      data-heat={level}
      sub={
        <span className="mt-1 grid gap-1">
          <MiniBar
            value={taps}
            max={maxTaps}
            fillClassName={dark ? 'bg-white' : 'bg-brand'}
            trackClassName={dark ? 'bg-white/20' : 'bg-surface-3'}
            textClassName={dark ? 'text-white' : 'text-ink-2'}
            text={`タブ内 ${roundPct(taps, maxTaps)}%`}
            aria-label={`${label} の回数`}
          />
          <MiniBar
            value={users}
            max={maxUsers}
            fillClassName={dark ? 'bg-white/55' : 'bg-viz-stage-1'}
            trackClassName={dark ? 'bg-white/20' : 'bg-surface-3'}
            textClassName={dark ? 'text-white' : 'text-ink-2'}
            text={`${users} 人`}
            aria-label={`${label} の人数`}
          />
        </span>
      }
      className="h-full min-w-28"
    />
  );
}

interface MiniBarProps {
  value: number;
  max: number;
  /** bg-* トークン由来クラス */
  fillClassName: string;
  trackClassName?: string;
  textClassName?: string;
  /** 右端の文字 */
  text: ReactNode;
  'aria-label'?: string;
}

/** 高さ 8px の細い単棒（RatioBar single の 14px は配置タイル内では太いため専用）。 */
function MiniBar({
  value,
  max,
  fillClassName,
  trackClassName = 'bg-surface-3',
  textClassName = 'text-ink-2',
  text,
  'aria-label': ariaLabel,
}: MiniBarProps) {
  return (
    <span className="flex items-center gap-2" aria-label={ariaLabel}>
      <span
        className={cn(
          'block h-2 min-w-0 flex-1 overflow-hidden rounded-md',
          trackClassName,
        )}
        data-testid="mini-bar-track"
      >
        <span
          className={cn('block h-full rounded-md', fillClassName)}
          style={{ width: `${widthPct(value, max)}%` }}
          data-testid="mini-bar-fill"
        />
      </span>
      <span
        className={cn(
          'ui-num shrink-0 whitespace-nowrap text-note tabular-nums',
          textClassName,
        )}
      >
        {text}
      </span>
    </span>
  );
}

interface StatCardProps {
  item: string | null;
  /** 当該 item の集計（タップ 0 のボタンは undefined → 0 表示）。 */
  stat?: RichmenuTapStat;
}

/** 「その他」用の集計タイル（ラベル + 延べ回数 + ユニーク人数）。S-134 v2: @kawai/ui の Tile。 */
function StatCard({ item, stat }: StatCardProps) {
  const label = itemLabel(item);
  const taps = stat?.taps ?? 0;
  const users = stat?.users ?? 0;
  return (
    <Tile
      size="sm"
      padding="sm"
      label={label}
      definition={label}
      value={taps}
      unit="回"
      sub={`${users} 人`}
      className="h-full min-w-28"
    />
  );
}

/** 順位の棒（全ボタン / 回数降順 / 配置外も含む）。 */
function RankList({ rows }: { rows: readonly RichmenuRankRow[] }) {
  const maxTaps = rows.reduce((m, r) => Math.max(m, r.taps), 0);
  const maxUsers = rows.reduce((m, r) => Math.max(m, r.users), 0);
  return (
    <div className="space-y-2.5">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <span className="text-label font-medium text-ink-2">順位（回数順）</span>
        <ChartLegend
          items={[
            { key: 'taps', label: '回数', className: 'bg-brand' },
            { key: 'users', label: '人数', className: 'bg-viz-stage-1' },
          ]}
          aria-label="順位の棒の凡例"
        />
      </div>
      <ol className="grid gap-2" data-testid="richmenu-rank">
        {rows.map((r) => (
          <li
            key={r.item ?? '__unknown__'}
            className="grid gap-1"
            data-testid={`richmenu-rank-${r.item ?? '__unknown__'}`}
          >
            <div className="flex items-baseline justify-between gap-3">
              <span className="min-w-0 truncate text-label font-medium text-ink">
                {itemLabel(r.item)}
                <span className="ml-1.5 text-note font-normal text-ink-3">
                  {r.tab ?? '—'}
                </span>
              </span>
              <span className="ui-num shrink-0 whitespace-nowrap text-note tabular-nums text-ink-2">
                {r.taps} 回 · {r.users} 人 · 1 人{' '}
                {r.perUser === null ? '—' : `${r.perUser.toFixed(1)}`} 回
              </span>
            </div>
            <span className="block h-2 w-full overflow-hidden rounded-md bg-surface-3">
              <span
                className="block h-full rounded-md bg-brand"
                style={{ width: `${widthPct(r.taps, maxTaps)}%` }}
                data-testid="rank-taps-fill"
              />
            </span>
            <span className="block h-2 w-full overflow-hidden rounded-md bg-surface-3">
              <span
                className="block h-full rounded-md bg-viz-stage-1"
                style={{ width: `${widthPct(r.users, maxUsers)}%` }}
                data-testid="rank-users-fill"
              />
            </span>
          </li>
        ))}
      </ol>
    </div>
  );
}
